/**
 * Filtering utility functions for FamiliesPage
 */

import { sortByName } from './sortingUtils';

/**
 * Filter families by active project
 * @param {Array} families - Array of family objects
 * @param {string} activeProjectId - Current active project ID
 * @param {Array} projects - Array of project objects
 * @returns {Array} Filtered array of families
 */
export function filterFamiliesByProject(families, activeProjectId, projects) {
    if (!activeProjectId) return families;
    
    const activeProject = projects.find(p => p.id.toString() === activeProjectId.toString());
    if (!activeProject) return [];
    
    const linkedFamilyIds = activeProject.linkedFamilies || [];
    return families.filter(f => 
        linkedFamilyIds.includes(f.id) || (f.parentId && linkedFamilyIds.includes(f.parentId))
    );
}

/**
 * Filter families by search query
 * @param {Array} families - Array of family objects
 * @param {string} searchQuery - Search string
 * @returns {Array} Filtered array of families
 */
export function filterFamiliesBySearch(families, searchQuery) {
    if (!searchQuery.trim()) return families;
    
    const q = searchQuery.toLowerCase();
    return families.filter(f => f.name.toLowerCase().includes(q));
}

/**
 * Filter features by search query
 * @param {Array} features - Array of feature objects
 * @param {string} searchQuery - Search string
 * @returns {Array} Filtered array of features
 */
export function filterFeaturesBySearch(features, searchQuery) {
    if (!searchQuery.trim()) return features;
    
    const q = searchQuery.toLowerCase();
    return features.filter(f => 
        f.name.toLowerCase().includes(q) || 
        (f.property?.displayText || '').toLowerCase().includes(q)
    );
}

/**
 * Filter features that are available to link (not already in the target family)
 * @param {Array} features - Array of all feature objects
 * @param {string} selectedFamilyId - Current selected family ID
 * @returns {Array} Filtered array of features available to link
 */
export function filterFeaturesAvailableToLink(features, selectedFamilyId) {
    return sortByName(
        features.filter(f => 
            !f.isGlobal && 
            (!f.sourceFamilyIds || !f.sourceFamilyIds.includes(selectedFamilyId))
        ),
        'name'
    );
}

/**
 * Combined filter for families list with project and search filtering
 * @param {Array} families - Array of family objects
 * @param {string} activeProjectId - Current active project ID
 * @param {Array} projects - Array of project objects
 * @param {string} searchQuery - Search string
 * @returns {Array} Filtered and sorted array of families
 */
export function filterFamilies(families, activeProjectId, projects, searchQuery) {
    let result = [...families];
    
    // Filter by project
    result = filterFamiliesByProject(result, activeProjectId, projects);
    
    // Filter by search
    result = filterFamiliesBySearch(result, searchQuery);
    
    return result;
}
